import { DateTime, DateTimeOptions } from "luxon";

export type DateType = string | number | Date | DateTime;

export const transformToDate = (date?: DateType | string | null | undefined, zone?: DateTimeOptions["zone"]) => {
  let tzName = zone;
  if (zone === "" || zone === null || zone === undefined) {
    tzName = undefined;
  }
  if (typeof date === "string") {
    const isoDate = DateTime.fromISO(date, { zone: tzName });
    return isoDate.isValid ? isoDate : null;
  }
  if (typeof date === "number") {
    const msDate = DateTime.fromMillis(date, { zone: tzName });
    return msDate.isValid ? msDate : null;
  }
  if (date instanceof Date) {
    const jsDate = DateTime.fromJSDate(date, { zone: tzName });
    return jsDate;
  }
  if (date instanceof DateTime && date.isValid) {
    return date;
  }
  return null;
};

// @deprecated Use transformToDate instead
export const transformToJSDate = (
  date?: DateType | string | null | undefined,
  zone?: DateTimeOptions["zone"],
): Date | null => {
  if (typeof date === "string") {
    const isoDate = DateTime.fromISO(date, { zone });
    return isoDate.isValid ? isoDate.toJSDate() : null;
  }
  if (typeof date === "number") {
    const msDate = DateTime.fromMillis(date, { zone });
    return msDate.isValid ? msDate.toJSDate() : null;
  }
  if (date instanceof Date) {
    return date;
  }
  if (date instanceof DateTime && date.isValid) {
    return date.toJSDate();
  }
  return null;
};

// @deprecated, you should be using the bu timezone
export const formatDate = (date?: DateType | null, format = "dd/MM/yyyy") => {
  const jsDate = transformToJSDate(date);

  if (!jsDate) {
    return "";
  }

  return DateTime.fromJSDate(jsDate).toUTC().toFormat(format, { locale: "utc" });
};

// @deprecated - Get the string date including locale data
export const formatDateIncLocale = (date?: DateType | null, format = "dd/MM/yyyy") => {
  const jsDate = transformToJSDate(date);

  if (!jsDate) {
    return "";
  }

  return DateTime.fromJSDate(jsDate).toFormat(format);
};

export const getInternationalDateFormat = (countryCode: string | undefined) => {
  switch (countryCode?.toLowerCase()) {
    case "us":
      return {
        shortDateFormat: "MM/dd/yyyy",
        longDateFormat: "LL-dd-y HH:mm",
        locale: "en-US",
        fullDateFormat: "LL-dd-y HH:mm z",
        fullDateTime: "LL-dd-y HH:mm:ss",
        dayDateFormat: "EEEE, LLLL dd, yyyy",
        timeOnlyFormat: "HH:mm",
      };
    default:
      return {
        shortDateFormat: "dd/MM/yyyy",
        longDateFormat: "dd-LL-y HH:mm",
        locale: "en-GB",
        fullDateFormat: "dd-LL-y HH:mm z",
        fullDateTime: "dd-LL-y HH:mm:ss",
        dayDateFormat: "EEEE, dd LLLL, yyyy",
        timeOnlyFormat: "HH:mm",
      };
  }
};

export const getStartOfDay = (date: string | Date) => {
  const dateTime = typeof date === "string" ? DateTime.fromISO(date) : DateTime.fromJSDate(date);
  return dateTime.startOf("day").toJSDate();
};

export const getEndOfDay = (date: string | Date) => {
  const dateTime = typeof date === "string" ? DateTime.fromISO(date) : DateTime.fromJSDate(date);
  return dateTime.endOf("day").toJSDate();
};

const toStartOfDayDateTime = (value: Date | string): DateTime =>
  (typeof value === "string" ? DateTime.fromISO(value) : DateTime.fromJSDate(value)).startOf("day");

/**
 * True if `date` is strictly after `reference`, compared at day granularity
 * (time-of-day and timezone offsets are ignored). Use when the two dates must
 * not be the same day (e.g. a death date must be after — not equal to — the DOB).
 */
export const isAfterDay = (date: Date, reference: Date | string): boolean =>
  toStartOfDayDateTime(date) > toStartOfDayDateTime(reference);

/** True if `date` is the same day as, or after, `reference` (day granularity). */
export const isSameOrAfterDay = (date: Date, reference: Date | string): boolean =>
  toStartOfDayDateTime(date) >= toStartOfDayDateTime(reference);

/** True if `date` is strictly before `reference` (day granularity). */
export const isBeforeDay = (date: Date, reference: Date | string): boolean =>
  toStartOfDayDateTime(date) < toStartOfDayDateTime(reference);

/**
 * True if `date` is the same day as, or before, `reference` (day granularity).
 * For a "not in the future" check, pass `new Date()` as the reference.
 */
export const isSameOrBeforeDay = (date: Date, reference: Date | string): boolean =>
  toStartOfDayDateTime(date) <= toStartOfDayDateTime(reference);
